import { IconButton, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Tooltip from '@mui/material/Tooltip';
import Flex from 'ui/Flex/Flex';
import { Column } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

type SortState = 'inactive' | 'active';
type SortDirection = 'asc' | 'desc' | '';

export type AgGridHeaderComponentProps = {
    column: Column;
    setSort: (sort: SortDirection, multiSort?: boolean) => void;
    enableSorting: boolean;
};

type Props = {
    expanded: boolean;
    toggleExpanded: () => void;
} & AgGridHeaderComponentProps;

export default function TotalProjectionsHeader({
    expanded,
    toggleExpanded,
    column,
    setSort,
    enableSorting,
}: Props) {
    const theme = useTheme();
    const { t } = useTranslation();

    const [ascSort, setAscSort] = useState<SortState>('inactive');
    const [noSort, setNoSort] = useState<SortState>('inactive');

    const onSortChanged = () => {
        setAscSort(column.isSortAscending() ? 'active' : 'inactive');
        setNoSort(
            !column.isSortAscending() && !column.isSortDescending()
                ? 'active'
                : 'inactive',
        );
    };

    useEffect(() => {
        column.addEventListener('sortChanged', onSortChanged);
        onSortChanged();
    }, []);

    const getNextSortOrder = (): SortDirection => {
        if (noSort === 'active') return 'asc';
        if (ascSort === 'active') return 'desc';
        return '';
    };

    const onSortRequested = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        setSort(getNextSortOrder(), event.shiftKey);
    };

    const sortIndex = column.getSortIndex();

    return (
        <>
            <Tooltip
                title={
                    expanded
                        ? t('projection.hideProjectionsPanel')
                        : t('projection.showProjectionsPanel')
                }
            >
                <IconButton size="small" onClick={toggleExpanded}>
                    <KeyboardArrowRightIcon
                        sx={{
                            transform: expanded ? '' : 'rotate(180deg)',
                        }}
                    />
                </IconButton>
            </Tooltip>
            <Flex
                onClick={(event) => {
                    if (!enableSorting) return;
                    onSortRequested(event);
                }}
                sx={{
                    textTransform: 'uppercase',
                    color: theme.palette.text.primary,
                    height: '100%',
                    width: '100%',
                }}
                align="center"
            >
                {t('projection.totalColumn')}
                {noSort === 'active' ? null : (
                    <>
                        {sortIndex && sortIndex > 0 ? (
                            <span
                                style={{
                                    paddingLeft: 'var(--ag-grid-size)',
                                    color: '#787878',
                                }}
                            >
                                {sortIndex + 1}
                            </span>
                        ) : null}
                        <span
                            style={{ paddingLeft: 'var(--ag-grid-size)' }}
                            className={`ag-icon ag-icon-${
                                ascSort === 'active' ? 'asc' : 'desc'
                            }`}
                            // eslint-disable-next-line react/no-unknown-property
                            unselectable="on"
                            role="presentation"
                        />
                    </>
                )}
            </Flex>
        </>
    );
}
